/**
 * Drawer — Panel that slides in from a screen edge for supplementary content.
 *
 * Drawer Component Guidelines
 * - Use Drawer for contextual panels that supplement the main view without fully
 *   blocking it. Prefer Modal when the user needs to make a focused decision.
 * - Use `direction="bottom"` for mobile-style action sheets and confirmations.
 *   Use side directions (`left`/`right`) for desktop detail panels and filters.
 * - Set `dismissable={false}` for flows where the user must complete or explicitly
 *   cancel (e.g. checkout steps). The drag handle is hidden automatically when
 *   `dismissable` is false.
 * - Prefer the simple `<Drawer>` for standard title/description/footer layouts.
 *   Switch to the composable API (`DrawerRoot` / `DrawerContent` / etc.) only when
 *   you need custom internal structure or non-standard animations.
 * @see https://vaul.emilkowal.ski Vaul documentation
 * @see https://ui.shadcn.com/docs/components/drawer shadcn/ui Drawer documentation
 */
import type { LayoutClassName } from "@replohq/lattice/types/layoutClassName";

import * as React from "react";

import {
  PortalThemeScope,
  usePortalContainer,
} from "@replohq/lattice/components/portal-theme/PortalTheme";
import { cn } from "@replohq/lattice/utils/cn";
import { Drawer as DrawerPrimitive } from "vaul";

function DrawerRoot({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) {
  return <DrawerPrimitive.Root data-slot="drawer" {...props} />;
}

interface DrawerTriggerProps<ClassName extends string = string>
  extends Omit<
    React.ComponentProps<typeof DrawerPrimitive.Trigger>,
    "className"
  > {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function DrawerTrigger<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: DrawerTriggerProps<ClassName>) {
  return (
    <DrawerPrimitive.Trigger
      data-slot="drawer-trigger"
      className={cn(className, UNSAFE_className)}
      {...props}
    />
  );
}

function DrawerPortal({
  children,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Portal>) {
  const container = usePortalContainer();
  return (
    <DrawerPrimitive.Portal
      data-slot="drawer-portal"
      container={container}
      {...props}
    >
      <PortalThemeScope>{children}</PortalThemeScope>
    </DrawerPrimitive.Portal>
  );
}

function DrawerClose({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Close>) {
  return <DrawerPrimitive.Close data-slot="drawer-close" {...props} />;
}

function DrawerOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Overlay>) {
  return (
    <DrawerPrimitive.Overlay
      data-slot="drawer-overlay"
      className={cn(
        "data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 bg-default/10 supports-backdrop-filter:backdrop-blur-xs fixed inset-0 z-50",
        className,
      )}
      {...props}
    />
  );
}

interface DrawerContentProps<ClassName extends string = string>
  extends Omit<
    React.ComponentProps<typeof DrawerPrimitive.Content>,
    "className"
  > {
  /**
   * Whether to show the drag handle bar at the top of the drawer.
   * Defaults to true for bottom drawers. Set to false for non-dismissable drawers.
   */
  showDragHandle?: boolean;
  /**
   * Layout class name applied to the content.
   */
  className?: LayoutClassName<ClassName>;
  /**
   * Escape hatch for non-layout classes on the content.
   */
  UNSAFE_className?: string;
}

function DrawerContent<ClassName extends string = string>({
  className,
  children,
  showDragHandle = true,
  UNSAFE_className,
  ...props
}: DrawerContentProps<ClassName>) {
  return (
    <DrawerPortal data-slot="drawer-portal">
      <DrawerOverlay />
      <DrawerPrimitive.Content
        data-slot="drawer-content"
        className={cn(
          // Fields default to a sheet background, which disappears against the
          // drawer's own sheet surface — re-tone them so they read as fields.
          // Invalid fields keep their rose background.
          "**:data-[slot=input]:not-aria-[invalid=true]:bg-surface **:data-[slot=textarea]:not-aria-[invalid=true]:bg-surface **:data-[slot=combobox-chips]:bg-surface",
          "bg-sheet text-default border-border shadow-button-selectable flex h-auto flex-col text-sm data-[vaul-drawer-direction=bottom]:inset-x-0 data-[vaul-drawer-direction=bottom]:bottom-0 data-[vaul-drawer-direction=bottom]:mt-24 data-[vaul-drawer-direction=bottom]:max-h-[90vh] data-[vaul-drawer-direction=bottom]:rounded-t-3xl data-[vaul-drawer-direction=bottom]:border-t data-[vaul-drawer-direction=left]:inset-y-0 data-[vaul-drawer-direction=left]:left-0 data-[vaul-drawer-direction=left]:w-3/4 data-[vaul-drawer-direction=left]:rounded-r-3xl data-[vaul-drawer-direction=left]:border-r data-[vaul-drawer-direction=right]:inset-y-0 data-[vaul-drawer-direction=right]:right-0 data-[vaul-drawer-direction=right]:w-3/4 data-[vaul-drawer-direction=right]:rounded-l-3xl data-[vaul-drawer-direction=right]:border-l data-[vaul-drawer-direction=top]:inset-x-0 data-[vaul-drawer-direction=top]:top-0 data-[vaul-drawer-direction=top]:mb-24 data-[vaul-drawer-direction=top]:max-h-[80vh] data-[vaul-drawer-direction=top]:rounded-b-3xl data-[vaul-drawer-direction=top]:border-b data-[vaul-drawer-direction=left]:sm:max-w-sm data-[vaul-drawer-direction=right]:sm:max-w-sm group/drawer-content fixed z-50",
          className,
          UNSAFE_className,
        )}
        {...props}
      >
        {showDragHandle && (
          <div className="bg-surface mx-auto mt-4 hidden h-1.5 w-[100px] shrink-0 rounded-full group-data-[vaul-drawer-direction=bottom]/drawer-content:block" />
        )}
        {children}
      </DrawerPrimitive.Content>
    </DrawerPortal>
  );
}

function DrawerHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="drawer-header"
      className={cn(
        "gap-1.5 px-5 group-data-[vaul-drawer-direction=bottom]/drawer-content:text-center group-data-[vaul-drawer-direction=top]/drawer-content:text-center md:gap-1.5 md:text-left flex flex-col",
        className,
      )}
      {...props}
    />
  );
}

function DrawerFooter<ClassName extends string = string>({
  className,
  UNSAFE_className,
  ...props
}: React.ComponentProps<"div"> & {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}) {
  return (
    <div
      data-slot="drawer-footer"
      className={cn(
        "gap-3 p-5 mt-auto flex flex-col",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

function DrawerTitle<ClassName extends string = string>({
  className,
  UNSAFE_className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Title> & {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}) {
  return (
    <DrawerPrimitive.Title
      data-slot="drawer-title"
      className={cn(
        "text-default font-editorial text-2xl font-normal italic leading-tight",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

function DrawerDescription({
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Description>) {
  return (
    <DrawerPrimitive.Description
      data-slot="drawer-description"
      className={cn("text-muted text-base", className)}
      {...props}
    />
  );
}

type DrawerDirection = "top" | "bottom" | "left" | "right";
type DrawerContentSurfaceProps = {
  style?: React.CSSProperties;
  "data-agent-theme"?: string;
  "data-replo-surface"?: string;
};

type DrawerProps<ClassName extends string = string> = React.PropsWithChildren<{
  title?: React.ReactNode;
  accessibleTitle?: React.ReactNode;
  description?: React.ReactNode;
  trigger?: React.ReactNode;
  footer?: React.ReactNode;
  /**
   * The direction from which the drawer slides in.
   * @default "bottom"
   */
  direction?: DrawerDirection;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Whether the drawer can be dismissed by clicking the overlay or dragging.
   * @default true
   */
  dismissable?: boolean;
  showDragHandle?: boolean;
  /**
   * Layout class name applied to the trigger wrapper.
   */
  className?: LayoutClassName<ClassName>;
  /**
   * Escape hatch for non-layout classes on the trigger wrapper.
   */
  UNSAFE_className?: string;
  contentProps?: DrawerContentSurfaceProps;
}>;

function Drawer<ClassName extends string>({
  title,
  accessibleTitle,
  description,
  trigger,
  footer,
  children,
  direction = "bottom",
  open,
  onOpenChange,
  dismissable = true,
  showDragHandle,
  className,
  UNSAFE_className,
  contentProps,
}: DrawerProps<ClassName>) {
  const shouldShowDragHandle = showDragHandle ?? dismissable;

  return (
    <DrawerRoot
      open={open}
      onOpenChange={onOpenChange}
      direction={direction}
      dismissible={dismissable}
    >
      {children && (
        <DrawerTrigger
          asChild
          className={className}
          UNSAFE_className={UNSAFE_className}
        >
          {trigger}
        </DrawerTrigger>
      )}
      <DrawerContent showDragHandle={shouldShowDragHandle} {...contentProps}>
        {!title && accessibleTitle && (
          <DrawerTitle>
            <span className="sr-only">{accessibleTitle}</span>
          </DrawerTitle>
        )}
        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
          {shouldShowDragHandle && (
            <div className="pointer-events-none absolute inset-x-0 top-0 h-6 bg-gradient-to-b from-sheet to-transparent z-10" />
          )}
          {(title || description) && (
            <DrawerHeader className="shrink-0 pt-8 pb-2">
              {title && <DrawerTitle>{title}</DrawerTitle>}
              {description && (
                <DrawerDescription>{description}</DrawerDescription>
              )}
            </DrawerHeader>
          )}
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="p-5">{children}</div>
          </div>

          <div className="relative">
            <div className="pointer-events-none absolute inset-x-0 bottom-full h-6 bg-gradient-to-t from-sheet to-transparent z-10" />
            {footer && (
              <DrawerFooter
                className="shrink-0"
                UNSAFE_className="border-t-0.5 border-border bg-sheet shadow-xs"
              >
                {footer}
              </DrawerFooter>
            )}
          </div>
        </div>
      </DrawerContent>
    </DrawerRoot>
  );
}

export {
  Drawer,
  DrawerRoot,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
};
